/**
 * The dock's geometry and its button set, as arithmetic rather than as markup.
 *
 * The voice surface used to be a full-screen takeover: one `inset: 0` rule and
 * no decisions to make. A dock has several — panel or sheet, which snap height,
 * which pair of buttons, which order — and every one of them is a rule about
 * safety or reachability rather than a style. So they live here, where a node
 * test can drive them with no browser, instead of inside a computed property in
 * a 2,800-line component where the only way to check the destructive button
 * order is to look at it.
 *
 * Nothing in this module touches `window`, `document` or a clock. The viewport
 * is an argument.
 */

import { exitCopy } from "./voiceTimers.js";

/**
 * The panel's width, and the width the page behind it is pushed by. One number,
 * exported, because the two must agree: a panel wider than the padding covers
 * the right-hand column of the grid, and a narrower one leaves a white gutter.
 */
export const PANEL_WIDTH_PX = 380;

/**
 * Below this the dock is a sheet. 1024 is the tablet-landscape line: at 380 px
 * a panel would be more than a third of a 1023 px viewport, and the grid it is
 * supposed to leave visible is the reason the dock exists.
 */
export const PANEL_MIN_WIDTH_PX = 1024;

/**
 * From this width the open panel PUSHES the page (padding-right on #app) so the
 * grid stays fully visible beside it; between PANEL_MIN_WIDTH_PX and this line the
 * panel overlays the page instead (Andrew, 2026-09-19: the push squeezed the grid
 * too much there). Mirrored by the media query in VoiceDock.vue's global style.
 */
export const PUSH_MIN_WIDTH_PX = 1280;

/** Whether an open panel pushes the page at this viewport width. */
export function pushesPage(viewportWidth) {
  return Number.isFinite(viewportWidth) && viewportWidth >= PUSH_MIN_WIDTH_PX;
}

/**
 * Sheet heights as fractions of the viewport, not pixels: a phone in landscape
 * and a phone in portrait want the same PROPORTION of the screen left over for
 * the grid, and half of a short viewport is the least that fits the state line, the chip and the thumb zone: measured at 390 × 844, 38 % pushed the Close button below the screen.
 *
 * Peek shows the state line, the card's first rows and the buttons. Expanded
 * shows the stage as well. There is no third snap and no free-form height —
 * two named stops are a thing a gloved thumb can hit, a continuum is not.
 */
export const SHEET_SNAPS = Object.freeze({ peek: 0.5, expanded: 0.82 });

/** Shortest first. `nextSnap` walks this, so the order IS the up/down axis. */
export const SNAP_ORDER = Object.freeze(["peek", "expanded"]);

const DEFAULT_SNAP = "expanded"; // Andrew, 2026-09-18: the sheet opens tall; half height cut the card off

const isSnap = (snap) => SNAP_ORDER.indexOf(snap) !== -1;

/**
 * Which shape the dock takes at a given viewport width.
 *
 * A non-numeric width answers `'sheet'`. That is the deliberate direction to
 * fail in: the sheet works at every width, and a panel guessed onto a phone
 * would cover most of the screen with no way back.
 *
 * @param {number} viewportWidth CSS pixels
 * @returns {'panel'|'sheet'}
 */
export function layoutFor(viewportWidth) {
  if (!Number.isFinite(viewportWidth)) return "sheet";
  return viewportWidth >= PANEL_MIN_WIDTH_PX ? "panel" : "sheet";
}

/**
 * Where the launcher that OPENS the dock lives, which is a different question
 * from what shape the dock takes once it is open.
 *
 * Three answers, and the third is why this is a function rather than a media
 * query. `'inline'` is the button in the search bar's own row, which is where
 * it has always been. `'floating'` is a fixed disc in the bottom-right corner,
 * because a control in the top bar of a phone is at the far end of a thumb's
 * travel and this app is used one-handed, in gloves, standing at a dewar.
 * `'hidden'` is the floating disc while the dock is open: the sheet is anchored
 * to the bottom of the same screen and the panel covers the same right-hand
 * edge, so the disc would be sitting underneath the surface it just opened,
 * asking to open it again.
 *
 * The inline placement needs BOTH a fine pointer and the room for the panel.
 * Either condition failing sends the button to the corner: a 1280 px touch
 * screen is still reached by thumb, and a 900 px desktop window still has the
 * dock as a sheet along the bottom, which is where a corner button belongs.
 * That is also why the pointer test is `coarsePointer` rather than "is this a
 * phone" — it is the same primary-pointer question `defaultMicMode` already
 * asks in `voiceDockPrefs.js`, and the two must not disagree about what kind of
 * hand is on the other end.
 *
 * A width that is not a number answers `'floating'`, the same direction
 * `layoutFor` fails in and for the same reason: the corner works at every
 * width, while an inline button guessed onto a 360 px screen crushes the search
 * input it shares a row with.
 *
 * @param {object} [options]
 * @param {boolean} [options.coarsePointer] the primary pointer is a finger
 * @param {number} [options.viewportWidth] CSS pixels
 * @param {boolean} [options.dockOpen] the dock is on screen
 * @returns {'inline'|'floating'|'hidden'}
 */
export function micPlacement({
  coarsePointer = false,
  viewportWidth,
  dockOpen = false,
} = {}) {
  const roomForPanel =
    Number.isFinite(viewportWidth) && viewportWidth >= PANEL_MIN_WIDTH_PX;
  // Stays exactly as it was, open or closed: it sits in the search bar, the
  // panel hangs from the bar's bottom edge, and nothing covers it.
  if (roomForPanel && !coarsePointer) return "inline";
  return dockOpen ? "hidden" : "floating";
}

/**
 * The next snap in a direction, clamped at both ends.
 *
 * Clamped, not wrapped. A drag upwards at the top of its travel must do
 * nothing; wrapping would collapse the sheet under a thumb that was asking for
 * more of it, which is the one outcome a drag gesture must never produce.
 *
 * @param {string} current a key of `SHEET_SNAPS`
 * @param {'up'|'down'|number} direction positive/`'up'` grows the sheet
 * @returns {string} a key of `SHEET_SNAPS`
 */
export function nextSnap(current, direction) {
  const from = isSnap(current) ? current : DEFAULT_SNAP;
  const step =
    direction === "up" || direction > 0
      ? 1
      : direction === "down" || direction < 0
        ? -1
        : 0;
  if (step === 0) return from;
  const index = SNAP_ORDER.indexOf(from) + step;
  if (index < 0) return SNAP_ORDER[0];
  if (index > SNAP_ORDER.length - 1) return SNAP_ORDER[SNAP_ORDER.length - 1];
  return SNAP_ORDER[index];
}

/**
 * A snap's height in pixels. Rounded, because a fractional pixel height on a
 * bordered sheet leaves a hairline of page showing under it on some devices.
 *
 * @param {string} snap
 * @param {number} viewportHeight
 * @returns {number}
 */
export function sheetHeightPx(snap, viewportHeight) {
  if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) return 0;
  const fraction = SHEET_SNAPS[isSnap(snap) ? snap : DEFAULT_SNAP];
  return Math.round(viewportHeight * fraction);
}

/**
 * The snap a released drag lands on: whichever stop the sheet is nearest,
 * measured as a fraction of the viewport.
 *
 * Nearest rather than "the one you were heading towards", because a drag that
 * stops halfway is a person who has found the height they wanted and is not
 * going to be told they meant the other one.
 *
 * @param {number} fraction current height / viewport height
 * @returns {string} a key of `SHEET_SNAPS`
 */
export function snapForFraction(fraction) {
  if (!Number.isFinite(fraction)) return DEFAULT_SNAP;
  let best = SNAP_ORDER[0];
  let bestDistance = Infinity;
  for (const snap of SNAP_ORDER) {
    const distance = Math.abs(SHEET_SNAPS[snap] - fraction);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = snap;
    }
  }
  return best;
}

/**
 * The sentence a closed session leaves on the state line.
 *
 * Only a session that ran out of time has one. `transport-lost`, `user`,
 * `confirmed` and `restart` all close with `kind: null` — nothing expired, so
 * there is nothing to name, and inventing "Stopped: …" copy for a connection
 * drop would tell the user a window closed when in fact the network went.
 *
 * `totalMs` matters for exactly one kind. The confirm line quotes its own
 * length, and that length is 20 s rather than 12 s whenever spoken replies are
 * off, so the caller passes the window it actually armed. Every other line
 * ignores the argument.
 *
 * @param {{kind?: string|null, totalMs?: number}|null} closed the session's `closed` payload
 * @param {number} [totalMs] the confirm window that actually ran
 * @returns {string} '' when nothing expired
 */
export function exitCopyFor(closed, totalMs) {
  const kind = closed && closed.kind;
  if (!kind) return "";
  // The window the session says it ran wins. `totalMs` is the caller's guess
  // from the CURRENT preference, kept only as a fallback for a close that does
  // not carry its own length: a "Spoken replies" toggle while the confirm
  // window was already counting would otherwise quote the wrong number.
  const ms =
    closed && Number.isFinite(closed.totalMs)
      ? closed.totalMs
      : Number.isFinite(totalMs)
        ? totalMs
        : undefined;
  return exitCopy(kind, ms);
}

/**
 * The line under a card that says where the user stands.
 *
 * Three states, and each is a different promise about what will happen next.
 *
 * A session that ENDED cannot apply anything until Resume voice re-arms it, so
 * the line leads with the fact that nothing happened and names the cause.
 *
 * A session whose STREAM was torn down for being idle is a different thing
 * entirely, and saying "Voice stopped" for it would be false: the microphone is
 * still open, the card still holds its preview hash and its idempotency key, and
 * Confirm is still a live button making an ordinary HTTP call. What is no longer
 * true is that the word "confirm" can be heard — that needs a stream, and the
 * next tap is what brings one back. So the line names both ways forward.
 *
 * With spoken replies ON the stream is held open for exactly that word
 * (`streamIdleAllowed`), so this line is not reachable under a card the user was
 * invited to speak to; it is keyed on the STREAM rather than on the preference
 * because the stream is the thing that decides whether a word can land, whatever
 * closed it.
 *
 * A TAP-ONLY card is a fourth state, and it is named before the stream is
 * consulted. In a shared-terminal lab the relay opens no spoken window for a
 * delete at all (`confirmMode: 'tap'`), so there is nothing counting down and
 * no word that can land — and the quiet line's "or tap the mic to speak" would
 * be inviting one that will not be taken. It says instead what the only way
 * forward is, and why there is only one.
 *
 * @param {{sessionEnded?: boolean, streaming?: boolean, tapOnly?: boolean}} [state]
 * @returns {{kind: 'stopped'|'quiet', text: string}}
 */
export function cardStatusLine({
  sessionEnded = false,
  streaming = true,
  tapOnly = false,
} = {}) {
  if (sessionEnded) {
    return {
      kind: "stopped",
      text: "Not applied. Voice stopped before you confirmed.",
    };
  }
  if (tapOnly) {
    return {
      kind: "quiet",
      text: "Tap Confirm to delete. This lab does not take a spoken confirm.",
    };
  }
  if (streaming === false) {
    return {
      kind: "quiet",
      text: "Nothing has changed yet. Tap Confirm, or tap the mic to speak.",
    };
  }
  return { kind: "quiet", text: "Nothing has changed yet." };
}

/**
 * The footer's buttons, in DOM order, for each of the four states the dock can
 * be in. One pair at most, always.
 *
 * The old footer had four near-identical templates stacking three and four
 * buttons into the thumb zone after a session ended. Which of them was on
 * screen was worked out from `preview && isDestructive`, `preview`,
 * `sessionEnded` and a fallthrough, and the answer to "can this card still be
 * confirmed" was spread across all four. Here it is one table:
 *
 * | preview | live | retry | buttons                  |
 * |---------|------|-------|--------------------------|
 * | yes     | yes  | —     | Confirm · Cancel         |
 * | yes     | no   | yes   | Confirm again · Close    |
 * | yes     | no   | no    | Resume voice · Close     |
 * | no      | yes  | —     | Stop voice               |
 * | no      | no   | —     | Start voice · Close      |
 *
 * The **Resume voice** row is the safety rule made structural. A stopped session
 * cannot apply anything — not by tap and not by voice — so the dock does not
 * render a confirm affordance at all until **Resume voice** has re-armed the
 * same card against a live session. A disabled "Confirm move" sitting under a
 * state word reading "Stopped" was the disagreement between word and affordance
 * that this packet exists to remove; leaving it in place, greyed, would keep the
 * disagreement and add a dead button to the thumb zone.
 *
 * `retry` is the ONE exception, and it is not a hole in that rule: it is the
 * case where the session ended BECAUSE OF this card's own confirm, the apply
 * then failed without an answer, and the dock has just told the user "if it did
 * not happen, confirm again — retrying is safe". Retrying is plain HTTP and
 * carries the SAME idempotency key, so it is a retry the server recognises
 * rather than a second command; the session was never what made it safe.
 * Without this row that sentence asked the user to buy a whole new billed relay
 * session — microphone prompt, handshake, ledger row — to press a button that
 * never touches the socket. "Confirm again" rather than "Confirm move", because
 * the thing being offered is the retry, not a fresh decision.
 *
 * @param {object} options
 * @param {boolean} options.preview a card is on screen
 * @param {boolean} options.live the session can still hear
 * @param {boolean} [options.retry] the apply's outcome was never learned
 * @param {boolean} [options.destructive] the card deletes something
 * @param {string} [options.confirmLabel] `CONFIRM_LABELS[intent]`
 * @returns {Array<{action: string, label: string, tone: string}>}
 */
export function footerButtons({
  preview,
  live,
  retry = false,
  destructive = false,
  confirmLabel = "Confirm",
  holdMode = false,
} = {}) {
  if (preview) {
    if (live) {
      return [
        {
          action: "confirm",
          label: confirmLabel,
          tone: destructive ? "danger" : "primary",
        },
        { action: "cancel", label: "Cancel", tone: "quiet" },
      ];
    }
    if (retry) {
      // Primary tone even on a destructive card: the box is already deleted or
      // already not, and the danger tone would ask the user to weigh a decision
      // they have made and cannot make twice.
      return [
        { action: "confirm", label: "Confirm again", tone: "primary" },
        { action: "close", label: "Close", tone: "quiet" },
      ];
    }
    return [
      { action: "resume", label: "Resume voice", tone: "primary" },
      { action: "close", label: "Close", tone: "quiet" },
    ];
  }
  if (live) {
    return [{ action: "stop", label: "Stop voice", tone: "quiet" }];
  }
  // Hold-to-talk: the hold button IS the way to start, so the footer offers
  // only the way out. Hands-free keeps its explicit Start.
  if (holdMode) {
    return [{ action: "close", label: "Close", tone: "quiet" }];
  }
  return [
    { action: "start", label: "Start voice", tone: "primary" },
    { action: "close", label: "Close", tone: "quiet" },
  ];
}

/**
 * Which way the footer's one flex container runs.
 *
 * DOM order is fixed — the acting button, then the way out — so the reading
 * order and the tab order say the same thing on every card. Only the PAINTED
 * order moves, and only in the one case that needs it.
 *
 * On a sheet the thumb rests at the bottom of the screen, so the bottom slot is
 * the one a fumbled tap in gloves lands in, and on a destructive card that slot
 * must hold Cancel. Column order already puts Cancel there, so the sheet does
 * not move.
 *
 * A panel has no thumb zone: it is a tall column on a desk, its footer is the
 * last thing down the page, and the pointer arrives from above. There the
 * bottom slot is the one under the cursor at the end of a read, so a
 * destructive card reverses to put Cancel above and Confirm at the far end.
 *
 * @param {{destructive?: boolean, layout?: string}} options
 * @returns {'column'|'column-reverse'}
 */
export function footerDirection({ destructive = false, layout = "sheet" } = {}) {
  return destructive && layout === "panel" ? "column-reverse" : "column";
}
